import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import React, { useRef, useMemo, useContext } from 'react';
import { useTheme, jsx, ThemeProvider } from '@emotion/react';
import { aurora } from '@indeed/ifl-themes';
import { IconProvider } from '@indeed/ifl-icons';
import merge from 'deepmerge';
import isEqual from 'react-fast-compare';
import { UseIdContext, defaultContext, UseIdProvider } from '@indeed/ihl-use-id';
import warnDeprecation from '../../utils/warnDeprecation.js';
import { LinkProvider } from '../LinkProvider/LinkProvider.js';
import '../../utils/warn.js';

const DEFAULT_THEME = aurora;
const isIndeedTheme = theme => theme._themeName === "indeed";
function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}
const isReact16or17 = React.version.startsWith("17.") || React.version.startsWith("16.");

/**
 * @see https://experience.indeed.design/components/ids/indeed-theme-provider
 */
const IndeedThemeProvider = _ref => {
  var _theme$meta;
  let {
    children,
    theme,
    useIdProviderProps,
    iconVariant,
    linkProviderProps
  } = _ref;
  const providedTheme = typeof theme === "object" ? theme : {};
  const prevTheme = useRef({});
  const parentTheme = useTheme();
  const baseTheme = useMemo(() => {
    return isIndeedTheme(parentTheme) ? parentTheme : isIndeedTheme(providedTheme) ? providedTheme : isEmpty(parentTheme) && isEmpty(providedTheme) ? DEFAULT_THEME : merge.all([DEFAULT_THEME, parentTheme]);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [parentTheme]);
  const prevMergedTheme = useRef(baseTheme);
  const mergedTheme = useMemo(() => {
    if (isEqual(providedTheme, prevTheme.current)) {
      return prevMergedTheme.current;
    }
    if (isEqual(providedTheme, baseTheme)) {
      return providedTheme;
    }
    prevTheme.current = theme;
    prevMergedTheme.current = merge.all([baseTheme, providedTheme]);

    // IFL-1350: Do not merge breakpoints together
    if (providedTheme.breakpoints) {
      prevMergedTheme.current.breakpoints = providedTheme.breakpoints;
    }
    return prevMergedTheme.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTheme, theme]);

  // Alias rawColors to colors for IFL 7 compatibility
  mergedTheme.rawColors = mergedTheme.colors;

  // When running React 16 or 17, the UseIdProvider requires a namespace.
  const namespaceProp = useIdProviderProps == null ? void 0 : useIdProviderProps.namespace;
  const randomNumberRef = useRef(null);
  const namespace = useMemo(() => {
    if (isReact16or17) {
      // generate a random 6-letter string to ensure no collisions when a namespaceProp isn't provided in React 16/17
      if (randomNumberRef.current === null && !namespaceProp) {
        const lowerCaseLetters = "abcdefghijklmnopqrstuvwxyz";
        randomNumberRef.current = "xxxxxx".split("").map(() => lowerCaseLetters.charAt(Math.random() * lowerCaseLetters.length)).join("");
      }
      return namespaceProp || `indeed-theme-provider-${randomNumberRef.current}`;
    }
    return undefined;
  }, [namespaceProp]);
  const idContext = useContext(UseIdContext);
  const createEmotionCacheProp = useIdProviderProps == null ? void 0 : useIdProviderProps.createEmotionCache;
  const shouldIncludeUseIdProvider = useMemo(() => {
    if (isReact16or17) {
      // On React 16/17, we only want to include UseIdProvider if we aren't already within UseIdContext
      return idContext === defaultContext;
    } else {
      // On React 18+, we only want to include UseIdProvider if the team is using emotion cache creation
      return !!createEmotionCacheProp;
    }
  }, [idContext, createEmotionCacheProp]);
  let contents = children;
  if (shouldIncludeUseIdProvider) {
    contents = jsx(UseIdProvider, _extends({}, useIdProviderProps, namespace ? {
      namespace
    } : {}), contents);
  }
  if (linkProviderProps) {
    contents = jsx(LinkProvider, linkProviderProps, contents);
  }
  const iconContext = {
    variant: iconVariant
  };
  if ((theme == null || (_theme$meta = theme.meta) == null ? void 0 : _theme$meta.name) === "auroraUplift") {
    warnDeprecation('The "auroraUplift" theme has been deprecated and will be removed in IFL v7. If you are on IFL 6, please continue to do use this theme. Switch to the "aurora" only after upgrading to IFL 7.0. See https://link.indeed.tech/ifl-7-prep-guide for mor details.');
  }
  return jsx(IconProvider, {
    value: iconContext
  }, jsx(ThemeProvider, {
    theme: mergedTheme
  }, contents));
};
IndeedThemeProvider.displayName = "IndeedThemeProvider";

export { IndeedThemeProvider };
